import type { EtlRunDetail, RunRecordSummary } from '../../types/etlRuns'
import type { RequestedRun } from '../../types/etlProgress'
import type { ExecutionDetail, ExecutionEntity } from '../../types/executions'
import { exactNumber } from '../../utils/format'

/**
 * The ETL Explorer's Dependencies tab, as pure logic: LOAD LEVELS, not a
 * dependency graph.
 *
 * What is recorded is only the order. Every entity's loader declares a
 * LevelNumber; WarehouseEltOrchestrator runs the levels in ascending order, one
 * entity at a time, and a failure at one level does not stop the next. Which
 * entities an entity actually reads from is recorded nowhere — so nothing here
 * may say one entity "needs" or "waits for" another. A lower level is a lower
 * level, and the copy says exactly that.
 *
 * The rows are Execution History's (GET api/observatory/executions/{runId}):
 * ordered by level, then name, at most 200, with `entityRowCount` the total and
 * `entityRowsTrimmed` saying whether retention has removed finished rows. An
 * entity row exists only once that entity starts. Each of those limits is
 * said as an integrity note rather than silently leaving a list short.
 */

/** One level's rows, in the order the server served them. */
export interface LevelGroup {
  level: number
  entities: ExecutionEntity[]
}

/** What the Dependencies tab lists for one entity run. */
export interface RunLevels {
  /** This entity's declared load level. */
  level: number
  /** The levels below this entity's, lowest first; each group keeps the served order. */
  lower: LevelGroup[]
  /** This entity's own row, or null when it is beyond the rows listed. */
  self: ExecutionEntity | null
  /** The other rows at this entity's level, in the served order — never this entity's own row. */
  sameLevel: ExecutionEntity[]
  /** How many listed rows sit at a level above this entity's. */
  higherCount: number
  /** What the run's recorded scope says about which levels ran in it; null for the whole warehouse. */
  scope: string | null
  /** Why the lists may be incomplete, in the order they are shown. Empty when nothing is known to be missing. */
  notes: string[]
}

/** Some finished rows are past retention (`entityRowsTrimmed: true`). */
export const NOTE_ROWS_TRIMMED =
  'Some of this run\'s entity rows have passed retention — succeeded rows are trimmed sooner than failed ones — '
  + 'so these lists are incomplete. An entity missing here is not evidence it did not run.'

/** No roll-up to compare the rows against (`entityRowsTrimmed: null`) on a run that is not running. */
export const NOTE_NO_ROLLUP =
  'This run has no completion roll-up, so whether any of its entity rows were trimmed cannot be told.'

/** A running run: rows appear as entities start. */
export const NOTE_STARTED_ONLY =
  'An entity appears here once it starts; those not yet reached are not listed.'

/** This entity's own row is not among the rows served. */
export const NOTE_OWN_ROW_ABSENT = 'This entity\'s own row is beyond the rows listed.'

/** The rows served are fewer than the rows the run still has. */
export function truncatedNote(listed: number, total: number): string {
  return `Showing the first ${exactNumber(listed)} of ${exactNumber(total)} entity rows, lowest level first.`
}

/** The scope line when nothing parseable was recorded — never a guess at the whole warehouse. */
export const SCOPE_NOT_RECORDED = 'No scope was recorded when this run was created.'

/** The same-level section's caption: within a level, only the start times say who went first. */
export const SAME_LEVEL_CAPTION =
  'Entities at one level run one after another; their start times give the order.'

/** Entity names compared as runFormat.ts's recordedForAnotherEntity compares them. */
function sameName(a: string, b: string): boolean {
  return a.toLowerCase() === b.toLowerCase()
}

/** "1 entity is" / "3 entities are". */
function entitiesAre(count: number, lead: string): string {
  return count === 1 ? `1 ${lead}entity is` : `${exactNumber(count)} ${lead}entities are`
}

/**
 * What the run's recorded scope says about which levels ran in it, read in the
 * order targetText reads it (entity, then whole warehouse, then module).
 *
 * - An entity job ran that one entity alone, so no other level ran in it —
 *   unless a later request was recorded inside the run, which is then said
 *   beside it with the count of rows for other entities.
 * - A module job ran only that module's entities, so an empty lower level may
 *   just be another module's.
 * - The whole warehouse needs no caveat: every level ran.
 * - Nothing recorded says so, rather than assuming the whole warehouse.
 */
export function scopeSentence(requested: RequestedRun | null, entities: readonly ExecutionEntity[]): string | null {
  if (!requested) return SCOPE_NOT_RECORDED
  if (requested.entity) {
    const target = requested.entity
    const created = `This run was created to load ${target} alone, so no other level ran in it.`
    const others = entities.filter((e) => !sameName(e.entityName, target)).length
    if (others === 0) return created
    return `${created} Yet ${entitiesAre(others, 'other ')} recorded in it: a later request was recorded inside `
      + 'this run — see its anomalies in Execution History.'
  }
  if (requested.isWholeWarehouse) return null
  if (requested.module) {
    return `This run was limited to the ${requested.module} module, so lower-level entities in other modules did not `
      + 'run in it.'
  }
  return SCOPE_NOT_RECORDED
}

/** Rows grouped by level, ascending; each group keeps the order the rows arrived in. */
function groupByLevel(rows: readonly ExecutionEntity[]): LevelGroup[] {
  const groups = new Map<number, ExecutionEntity[]>()
  for (const row of rows) {
    const group = groups.get(row.levelNumber)
    if (group) group.push(row)
    else groups.set(row.levelNumber, [row])
  }
  return [...groups.entries()]
    .sort(([a], [b]) => a - b)
    .map(([level, entities]) => ({ level, entities }))
}

/**
 * Why the lists may be short. Each note is said if and only if its condition
 * holds, and none of them is inferred from an empty list:
 * - retention trimmed finished rows (`entityRowsTrimmed: true`);
 * - no roll-up to tell (`null`) on a run that is not running — a running run
 *   has no roll-up by construction, and its own note says what is missing;
 * - a running run lists only the entities that have started;
 * - the server's 200-row cap cut the list;
 * - this entity's own row is not among those served.
 *
 * `outcome` is the RUN's, not this entity's: whether rows are still to appear
 * depends on the pass, not on one of its entities.
 */
function integrityNotes(
  execution: Pick<ExecutionDetail, 'entities' | 'entityRowCount' | 'entityRowsTrimmed' | 'outcome'>,
  self: ExecutionEntity | null,
): string[] {
  const notes: string[] = []
  if (execution.entityRowsTrimmed === true) notes.push(NOTE_ROWS_TRIMMED)
  if (execution.entityRowsTrimmed == null && execution.outcome !== 'running') notes.push(NOTE_NO_ROLLUP)
  if (execution.outcome === 'running') notes.push(NOTE_STARTED_ONLY)
  if (execution.entities.length < execution.entityRowCount) {
    notes.push(truncatedNote(execution.entities.length, execution.entityRowCount))
  }
  if (self === null) notes.push(NOTE_OWN_ROW_ABSENT)
  return notes
}

/**
 * Where one entity run sits among its run's load levels: the levels below it,
 * the others at its own level, how many above, what the recorded scope says,
 * and why any of those lists may be incomplete.
 *
 * `run.runId` is the ENTITY run id — the row's `entityRunId` — so this entity's
 * own row is found by that id, the Explorer's grain, and never by name.
 */
export function runLevels(
  run: Pick<EtlRunDetail, 'runId' | 'levelNumber' | 'requested'>,
  execution: Pick<ExecutionDetail, 'entities' | 'entityRowCount' | 'entityRowsTrimmed' | 'outcome'>,
): RunLevels {
  const level = run.levelNumber
  const rows = execution.entities
  const self = rows.find((e) => e.entityRunId === run.runId) ?? null

  return {
    level,
    lower: groupByLevel(rows.filter((e) => e.levelNumber < level)),
    self,
    sameLevel: rows.filter((e) => e.levelNumber === level && e.entityRunId !== run.runId),
    higherCount: rows.filter((e) => e.levelNumber > level).length,
    scope: scopeSentence(run.requested, rows),
    notes: integrityNotes(execution, self),
  }
}

/** The tab's opening paragraph: what a load level is, and that it is not a statement of need. */
export function levelsIntro(run: Pick<EtlRunDetail, 'entityName' | 'levelNumber'>): string {
  const name = run.entityName
  return `Every entity's loader declares a load level; ${name}'s is ${run.levelNumber}. A warehouse run loads level 0 `
    + 'first, then each level above it, one entity at a time. That order is all that is recorded: which entities '
    + `${name} reads from is not, so an entity listed below is not necessarily one it needs.`
}

/**
 * Why a lower level's failure does not stop this one, and what this entity run
 * counted as a result. The served caveat is appended when there is one: the
 * counter is the final attempt's only.
 */
export function unresolvedKeysText(records: Pick<RunRecordSummary, 'keysUnresolved' | 'caveat'>): string {
  const n = records.keysUnresolved
  const counted = `This entity run recorded ${exactNumber(n)} unresolved ${n === 1 ? 'key' : 'keys'}.`
  const text = 'A failure at a lower level does not stop the run. Rows that point at something not yet loaded are '
    + 'still loaded, with the key counted as unresolved, and a later run fills it in. '
    + counted
  return records.caveat ? `${text} ${records.caveat}` : text
}

/** The lower section's empty state — plain text, with no status: an empty level is not a finding. */
export function nothingBelowText(level: number): string {
  return `Nothing below level ${level} is recorded in this run.`
}

/** The same-level section's empty state. */
export function aloneAtLevelText(level: number): string {
  return `No other entity at level ${level} is recorded in this run.`
}

/** The footer's lead, before "— see run {runKey}." */
export function higherLevelsLead(count: number): string {
  if (count === 0) return 'No entity at a higher level is recorded in this run'
  return count === 1
    ? '1 entity at a higher level is recorded in this run'
    : `${exactNumber(count)} entities at higher levels are recorded in this run`
}
